























































































import PreselectTemplateCard from "@/components/partner/PreselectTemplateCard.vue";
import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { PreselectTemplateKeysEnum } from "@/lib/enum/preselect-template-keys.enum";
import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import { $t } from "@/lib/utility/t";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { ActivityLog } from "@/models/activity-log.entity";
import { IReference, Reference } from "@/models/reference.entity";
import { ISharedContent, SharedContent } from "@/models/shared-content.entity";
import { ISnapshotSignRequest, SnapshotSignRequest } from "@/models/snapshot-sign-request.entity";
import { ISnapshot } from "@/models/snapshot.entity";
import snapshotSignRequestService from "@/services/mrfiktiv/services/snapshot-sign-request.service";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";

enum StepEnum {
  ENTER_EMAILS,
  SEND_MAIL,
  SUCCESS
}

@Component({
  components: { ConfirmActionDialog, PreselectTemplateCard }
})
export default class CreateSnapshotSignRequestDialog extends mixins(PermissionMixin, RulesMixin) implements IDialog {
  readonly StepEnum = StepEnum;
  readonly PreselectTemplateKeysEnum = PreselectTemplateKeysEnum;

  @Prop({ required: true }) partnerId!: string;
  @Prop({ required: true }) handoverId!: string;
  @Prop({ required: true }) snapshotId!: string;
  @Prop() snapshot!: ISnapshot;

  // Dialog state
  isDialogActive = false;
  step: StepEnum = StepEnum.ENTER_EMAILS;
  isLoading = false;
  isCreating = false;
  isSending = false;
  isFormValid = false;

  // Inputs
  email = "";

  get emailSuggestions() {
    return this.snapshot.emailSuggestions;
  }

  // Created data
  created: { email: string; request: ISnapshotSignRequest; sharedContent: ISharedContent }[] = [];

  get defaultLanguage() {
    return (this.$i18n.locale as LanguageCodeEnum) || LanguageCodeEnum.DE;
  }

  get height() {
    return `${this.$vuetify.breakpoint.height - 240}px`;
  }

  get stepTitle() {
    switch (this.step) {
      case StepEnum.ENTER_EMAILS:
        return this.$t("snapshot.signRequest.dialogTitle");
      case StepEnum.SEND_MAIL:
        return this.$t("snapshot.signRequest.send");
      case StepEnum.SUCCESS:
        return this.$t("snapshot.signRequest.success");
      default:
        return "";
    }
  }

  get leftText() {
    switch (this.step) {
      case StepEnum.ENTER_EMAILS:
        return this.$t("close");
      case StepEnum.SEND_MAIL:
        return this.$t("common.verbs.skip");
      case StepEnum.SUCCESS:
        return this.$t("close");
      default:
        return this.$t("back");
    }
  }

  get rightText() {
    switch (this.step) {
      case StepEnum.ENTER_EMAILS:
        return this.$t("snapshot.signRequest.continue");
      case StepEnum.SEND_MAIL:
        return this.$t("snapshot.signRequest.send");
      case StepEnum.SUCCESS:
        return this.$t("snapshot.signRequest.createAnother");
      default:
        return this.$t("snapshot.signRequest.next");
    }
  }

  get isRightDisabled() {
    switch (this.step) {
      case StepEnum.ENTER_EMAILS:
        return !this.email || !this.isFormValid || this.isCreating;

      case StepEnum.SEND_MAIL:
        return this.isSending || !this.created.length;
      default:
        return false;
    }
  }

  get partner() {
    return PartnerModule.partner;
  }

  get user() {
    return UserModule.user;
  }

  open() {
    this.isDialogActive = true;
    this.reset();
  }

  openForExisting(snapshotSignRequest: ISnapshotSignRequest, sharing: ISharedContent) {
    this.reset();
    const email = (sharing?.emailAddresses && sharing.emailAddresses[0]) || this.email || "";
    this.created = [{ email, request: snapshotSignRequest, sharedContent: sharing }];
    this.isDialogActive = true;
    this.step = StepEnum.SEND_MAIL;
  }

  close() {
    this.isDialogActive = false;
    this.reset();
  }

  reset() {
    this.step = StepEnum.ENTER_EMAILS;
    this.isLoading = false;
    this.isCreating = false;
    this.isSending = false;
    this.isFormValid = false;
    this.created = [];
  }

  templateContext(shared: ISharedContent) {
    return { url: shared?.url, snapshot: this.snapshot, partner: this.partner, user: this.user };
  }

  templateRefs(shared: ISharedContent): IReference[] {
    // Reference the handover to provide context to recipients
    const refs: IReference[] = [];
    if (this.handoverId) refs.push(new Reference({ refId: this.handoverId, refType: BackendResourceEnum.HANDOVER }));
    return refs;
  }

  copySharedContent(sc: ISharedContent) {
    sc.copyUrl();
  }

  sharedDate(req: ISnapshotSignRequest) {
    const ts = req?.timestamp?.created;
    return ts ? simpleDoubleDigitDate(ts) : "";
  }

  async onRightClick() {
    switch (this.step) {
      case StepEnum.ENTER_EMAILS:
        await this.createAll();
        break;
      case StepEnum.SEND_MAIL:
        await this.sendAll();
        this.step = StepEnum.SUCCESS;
        break;
      case StepEnum.SUCCESS:
        // send another -> back to first step
        this.step = StepEnum.ENTER_EMAILS;
        this.created = [];
        this.email = "";
        break;
    }
  }

  onLeftClick() {
    switch (this.step) {
      case StepEnum.ENTER_EMAILS:
        this.close();
        break;
      case StepEnum.SEND_MAIL:
        // Skip sending mail, go back to start
        this.step = StepEnum.ENTER_EMAILS;
        this.created = [];
        this.email = "";
        break;
      case StepEnum.SUCCESS:
        this.close();
        break;
    }
  }

  async createAll() {
    if (!this.email) return;
    this.isLoading = true;
    this.isCreating = true;
    this.created = [];

    try {
      const req = await new SnapshotSignRequest({
        partnerId: this.partnerId,
        handoverId: this.handoverId,
        snapshotId: this.snapshotId,
        status: RequestStatusEnum.DRAFT
      }).create();

      const shared = new SharedContent({
        partnerId: this.partnerId,
        title: $t("snapshot.signRequest.sharedTitle"),
        description: "",
        emailAddresses: [this.email],
        refs: [new Reference({ refId: req.id, refType: BackendResourceEnum.SNAPSHOT_SIGN_REQUEST })]
      });
      await shared.create();

      await new ActivityLog().createSnapshotSignRequest({
        partnerId: this.partnerId,
        snapshotId: this.snapshotId,
        handoverId: this.handoverId,
        snapshotSignRequestId: req.id
      });

      this.$emit("created", req);

      this.created.push({ email: this.email, request: req, sharedContent: shared });

      this.step = StepEnum.SEND_MAIL;
    } catch (e) {
      handleError(e);
    } finally {
      this.isCreating = false;
      this.isLoading = false;
    }
  }

  async sendAll() {
    try {
      this.isSending = true;
      const ref = this.$refs.preselectTemplateCard as PreselectTemplateCard;
      await ref.send();
      const req = this.created[0]?.request;
      try {
        if (req?.partnerId && req?.id) {
          await snapshotSignRequestService.updateStatus(req.partnerId, req.id, { status: RequestStatusEnum.REQUESTED });
          req.status = RequestStatusEnum.REQUESTED;
        }
      } catch (e) {
        this.$log.error(e);
      }
    } catch (e) {
      this.$log.error(e);
    } finally {
      this.isSending = false;
    }
  }
}
